import React from 'react';
import { Link } from 'react-router-dom';
import './TermsAndConditions.css';
import Footer from '../Footer';
import { SUPPORT_EMAIL } from '../../constants/supportContact';

const sections = [
  {
    id: 't1',
    navLabel: '1 · Eligibility',
    title: '1 · Eligibility and account basics',
    description:
      "Creating a Haastia account confirms that you're at least 18, that what you've told us is accurate, and that you'll keep your login to yourself.",
    bullets: [
      'Share truthful business details, pricing and availability.',
      'Tell us straight away if you think someone else has got into your account.',
      "One person or business shouldn't hold several accounts for the same role without written approval.",
    ],
  },
  {
    id: 't2',
    navLabel: '2 · Using Haastia',
    title: '2 · Using Haastia',
    description:
      'Clients agree to book and pay through Haastia. Professionals agree to deliver what their listing describes and to hold whatever licenses their work requires.',
    bullets: [
      'No illegal, harmful or discriminatory use of the platform.',
      'Only take bookings you can honor, and update your calendar when something changes.',
      'Harassment, hate speech or unsafe behavior gets an account removed.',
    ],
  },
  {
    id: 't3',
    navLabel: '3 · Payments',
    title: '3 · Payments, fees and cancellations',
    description:
      "Stripe processes payments. Subscription fees for professionals and service payments for clients follow Haastia's published pricing.",
    bullets: [
      'Keep transactions inside Haastia to stay covered by our policies.',
      'Cancelled appointments follow the windows shown at booking.',
      'A business may substitute another qualified professional for your appointment. If that happens we\'ll notify you, and you may cancel for a full refund at any time before the appointment starts.',
      'Chargebacks or disputes may put an account on hold while we look into it.',
    ],
  },
  {
    id: 't4',
    navLabel: '4 · Privacy',
    title: '4 · Privacy and data',
    description:
      'We use your information to run Haastia and to meet our legal obligations. The privacy policy has the full detail.',
    bullets: [
      'We never sell your personal information.',
      'We share data with processors like Stripe only to deliver core features.',
      'You can ask us to delete your account; some records are kept where the law requires it.',
    ],
    cta: { label: 'Read the privacy policy', to: '/privacy' },
  },
  {
    id: 't5',
    navLabel: '5 · Changes',
    title: '5 · Changes and contact',
    description:
      'We may update these terms. Carrying on using Haastia after an update means you accept the new version.',
    bullets: [
      'Material changes come with an email or an in-app banner.',
      'If you disagree with an update, stop using Haastia and close your account.',
    ],
    highlighted: true,
    cta: { label: 'Ask us about the terms', to: '/contact' },
  },
  {
    id: 't6',
    navLabel: '6 · Accepting',
    title: '6 · Accepting the terms',
    paragraphs: [
      'By selecting "I agree to the Terms and Conditions" when signing up, you confirm that you have read and understand these commitments. You also agree that Haastia may communicate electronically with you about your account, bookings, subscriptions, and policy updates.',
      'If you are agreeing to these Terms on behalf of a business or organization, you represent that you have authority to bind that entity.',
    ],
  },
];

const TermsAndConditions = () => (
  <>
    <main className="terms-page">
      <section className="site-section site-section--hero terms-hero">
        <div className="site-inner">
          <div className="terms-hero__copy">
            <p className="eyebrow">Terms &amp; conditions</p>
            <h1>What we ask of you, and what you can ask of us.</h1>
            <p className="lead terms-hero__lead">
              These terms apply to everyone using Haastia — the professionals running a business on
              it and the clients booking through it. They sit alongside any policies you agree to
              during onboarding.
            </p>
            <p className="terms-hero__meta">
              Last updated 18 September 2026 · Questions go to{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>
          </div>
        </div>
      </section>

      <section className="site-section site-section--flush-top terms-content">
        <div className="site-inner">
          <div className="terms-layout">
            <nav className="terms-nav" aria-label="Sections">
              <p className="eyebrow eyebrow--muted">Sections</p>
              {sections.map((section) => (
                <a href={`#${section.id}`} key={section.id}>
                  {section.navLabel}
                </a>
              ))}
            </nav>

            <div className="terms-articles">
              {sections.map((section) => (
                <article
                  id={section.id}
                  key={section.id}
                  className={`panel terms-article${section.highlighted ? ' panel--surface' : ''}`}
                >
                  <h2>{section.title}</h2>
                  {section.description && <p>{section.description}</p>}
                  {section.paragraphs &&
                    section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                  {section.bullets && (
                    <ul className="dot-list">
                      {section.bullets.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.cta && (
                    <Link className="btn btn-primary terms-article__cta" to={section.cta.to}>
                      {section.cta.label}
                    </Link>
                  )}
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default TermsAndConditions;
